import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, IconButton, Box, Divider, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { StateManager } from '../utilities/stateManager';
import RequestManager, { getCheckPdfUrl } from '../utilities/requestManager';
import { useAutoZ, ModalStackProvider } from '../utilities/ModalStackContext';
import moment from 'moment';

// Global admin-only "write a check" dialog. Opened from anywhere a purchase /
// expense / NTO object is interfaced, via StateManager.openWriteCheckModal(...).
// Groups the incoming purchases by payee (vendor), one editable check per payee,
// creates each check through the Jarvis check API, marks the referenced
// purchases paid, and opens each PDF in a new tab to print.
export default function WriteCheckModal({ open, purchases = [], onClose }) {
  const myZ = useAutoZ(open, (purchases || []).map(p => p.id).join(','));
  const [checks, setChecks] = React.useState([]);
  const [saving, setSaving] = React.useState(false);

  // Build one check per payee whenever the dialog opens with new purchases.
  React.useEffect(() => {
    if (!open) return;
    const groups = {};
    for (const p of purchases) {
      const payee = (p.vendor || '').trim();
      const key = payee.toUpperCase() || '(no payee)';
      if (!groups[key]) groups[key] = { payee, purchaseIds: [], amount: 0, memos: [] };
      groups[key].purchaseIds.push(p.id);
      groups[key].amount += Number(p.amount) || 0;
      if (p.memo) groups[key].memos.push(p.memo);
    }
    setChecks(Object.values(groups).map(g => ({
      payee: g.payee,
      amount: g.amount.toFixed(2),
      memo: g.memos[0] || '',
      date: moment().format('YYYY-MM-DD'),
      purchaseIds: g.purchaseIds,
    })));
  }, [open, purchases]);

  if (!StateManager.isAdmin()) return null;

  const updateCheck = (idx, field) => (e) => {
    const value = e.target.value;
    setChecks(prev => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const handleSubmit = async () => {
    const invalid = checks.find(c => !c.payee || !(Number(c.amount) > 0));
    if (invalid) {
      StateManager.setAlertAndOpen('Each check needs a payee and a positive amount', 'warning');
      return;
    }

    setSaving(true);
    StateManager.setLoading(true);
    try {
      const checkRequests = checks.map(c => ({
        payee: c.payee,
        amount: c.amount,
        memo: c.memo,
        date: c.date,
        purchase_id: c.purchaseIds,
      }));

      const res = await RequestManager.post({
        function: 'jarvisWriteChecks',
        variables: { checkRequests, keepPayable: false },
      });

      if (!res || res.success === false) {
        throw new Error(res && res.message ? res.message : 'Check creation failed');
      }

      const checkIds = res.checkIds || [];
      StateManager.setAlertAndOpen(`Created ${checkIds.length} check(s)`, 'success');
      checkIds.forEach(id => window.open(getCheckPdfUrl(id), '_blank'));
      if (typeof StateManager.updateData === 'function') StateManager.updateData();
      onClose();
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen(`Could not create checks: ${err.message}`, 'error');
    } finally {
      setSaving(false);
      StateManager.setLoading(false);
    }
  };

  const total = checks.reduce((s, c) => s + (Number(c.amount) || 0), 0);

  return (
    <ModalStackProvider value={myZ}>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: myZ }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Write Check{checks.length > 1 ? 's' : ''}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {checks.length === 0 && (
          <Typography color="text.secondary">No expenses selected.</Typography>
        )}
        {checks.map((c, idx) => (
          <Box key={idx} sx={{ mb: idx < checks.length - 1 ? 2 : 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  fullWidth size="small" label="Payee"
                  value={c.payee} onChange={updateCheck(idx, 'payee')}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth size="small" label="Amount" type="number"
                  value={c.amount} onChange={updateCheck(idx, 'amount')}
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth size="small" label="Memo"
                  value={c.memo} onChange={updateCheck(idx, 'memo')}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth size="small" label="Date" type="date"
                  InputLabelProps={{ shrink: true }}
                  value={c.date} onChange={updateCheck(idx, 'date')}
                />
              </Grid>
              <Grid item xs={12}>
                <Chip size="small" variant="outlined"
                  label={`${c.purchaseIds.length} expense${c.purchaseIds.length > 1 ? 's' : ''}`} />
              </Grid>
            </Grid>
            {idx < checks.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Typography sx={{ pl: 2 }} variant="body2" color="text.secondary">
          Total: ${total.toFixed(2)}
        </Typography>
        <Box>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving || checks.length === 0}>
            {saving ? 'Creating...' : `Create & Print`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
    </ModalStackProvider>
  );
}
